import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '../store/client.store.js';
import { useAuthStore } from '../store/auth.store.js';
import { apiGet, apiPost, apiPatch, apiDelete } from '../api/client.js';

export const useOrphanTerminals = () => {
  const user = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  const url = user?.role === 'superadmin'
    ? (selectedClientId ? `/clients/${selectedClientId}/mp-map` : null)
    : '/mp-map';
  return useQuery({
    queryKey: ['mp-map-orphan-terminals', selectedClientId],
    queryFn: async () => {
      const map = await apiGet(url);
      return map?.orphan_terminals ?? [];
    },
    enabled: !!url,
    staleTime: 30_000,
  });
};

export const useTerminals = () => {
  const user = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  const url = user?.role === 'superadmin'
    ? (selectedClientId ? `/clients/${selectedClientId}/terminals` : null)
    : '/terminals';
  return useQuery({
    queryKey: ['terminals', selectedClientId],
    queryFn: () => apiGet(url),
    enabled: !!url,
  });
};

export const useCreateTerminal = () => {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  return useMutation({
    mutationFn: (data) => {
      const url = user?.role === 'superadmin' && selectedClientId
        ? `/clients/${selectedClientId}/terminals`
        : '/terminals';
      return apiPost(url, data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['terminals'] }),
  });
};

export const useAssignTerminal = (terminalId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ posId }) => apiPatch(`/terminals/${terminalId}/assign`, { posId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['terminals'] }),
  });
};

export const useUpdateTerminal = (terminalId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiPatch(`/terminals/${terminalId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['terminals'] }),
  });
};

export const useChangeTerminalMode = (terminalId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (operatingMode) => apiPatch(`/terminals/${terminalId}/operating-mode`, { operatingMode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['terminals'] }),
  });
};

export const useDeleteTerminal = (terminalId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/terminals/${terminalId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['terminals'] }),
  });
};
